import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stepper,
    Step,
    StepLabel,
    Typography
} from '@mui/material';
import { UserSelectionStep } from './UserSelectionStep';
import { PermissionsConfigurationStep } from './PermissionsConfigurationStep';
import { ConfirmationStep } from './ConfirmationStep';
import { useUserPermissions } from './hooks/useUserPermissions';
import { useNotification } from '../../../../components/index';

const steps = ['Выбор пользователей', 'Настройка прав', 'Подтверждение'];

const CustomStepIcon = (props) => {
    const { active, completed, icon } = props;

    return (
        <div
            className={`custom-step-icon ${completed ? 'completed' : active ? 'active' : ''}`}
        >
            {completed ? '✓' : icon}
        </div>
    );
};

export const AddUsersDialog = ({
    open,
    onClose,
    availableUsers,
    loadingAvailableUsers,
    originalFiles,
    getDefaultPermissionsForRole,
    onUserTaskAccessAdded,
    handleAddMultipleUserTaskAccesses,
    currentUserAccess,
    onSendNotifications,
    theme
}) => {
    const { canConfigureFilePermissions } = useUserPermissions(currentUserAccess);
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userPermissions, setUserPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const { showError } = useNotification();

    const handleClose = () => {
        setCurrentStep(0);
        setSelectedUsers([]);
        setUserPermissions({});
        setLoading(false);
        onClose();
    };

    useEffect(() => {
        if (open) {
            setCurrentStep(0);
            setSelectedUsers([]);
            setUserPermissions({});
            setLoading(false);
        }
    }, [open]);

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <UserSelectionStep
                        availableUsers={availableUsers}
                        loadingAvailableUsers={loadingAvailableUsers}
                        selectedUsers={selectedUsers}
                        onSelectedUsersChange={setSelectedUsers}
                        onUserPermissionsChange={setUserPermissions}
                    />
                );
            case 1:
                return (
                    <PermissionsConfigurationStep
                        selectedUsers={selectedUsers}
                        availableUsers={availableUsers}
                        userPermissions={userPermissions}
                        onUserPermissionsChange={setUserPermissions}
                        originalFiles={originalFiles}
                        getDefaultPermissionsForRole={getDefaultPermissionsForRole}
                        currentUserAccess={currentUserAccess}
                    />
                );
            case 2:
                return (
                    <ConfirmationStep
                        selectedUsers={selectedUsers}
                        availableUsers={availableUsers}
                        userPermissions={userPermissions}
                        originalFiles={originalFiles}
                        currentUserAccess={currentUserAccess}
                        theme={theme}
                    />
                );
            default:
                return null;
        }
    };

    const handleAddUserAccesses = async () => {
        setLoading(true);
        try {
            const usersData = selectedUsers.map(userGuid => {
                const user = availableUsers.find(u => u.guid === userGuid);
                const userPerms = userPermissions[userGuid] || {
                    role: 'Viewer',
                    advancedMode: false,
                    customCanManageTeam: false,
                    filePermissions: {}
                };

                const fileAccesses = originalFiles.map(file => {
                    const fileKey = `${file.fileName}_v${file.fileVersion}`;
                    const isAccessible = canConfigureFilePermissions(file);

                    // Если файл недоступен - ВСЕГДА false
                    if (!isAccessible) {
                        return {
                            baseFileId: file.baseFileId,
                            canRead: false,
                            canEdit: false,
                            canDeleteOwnVersions: false,
                            canDeleteAllVersions: false
                        };
                    }

                    // Если файл доступен - берем настройки пользователя или права по умолчанию
                    let permissions;
                    if (userPerms.filePermissions && userPerms.filePermissions[fileKey]) {
                        permissions = userPerms.filePermissions[fileKey];
                    } else {
                        permissions = getDefaultPermissionsForRole(userPerms.role);
                    }

                    return {
                        baseFileId: file.baseFileId,
                        canRead: permissions.canRead,
                        canEdit: permissions.canEdit,
                        canDeleteOwnVersions: permissions.canDeleteOwnVersions,
                        canDeleteAllVersions: permissions.canDeleteAllVersions
                    };
                });

                return {
                    user: user,
                    role: userPerms.role,
                    canManageTeam: userPerms.advancedMode ?
                        userPerms.customCanManageTeam :
                        (userPerms.role === 'Admin' || userPerms.role === 'Creator'),
                    fileAccess: fileAccesses
                };
            });

            const success = await handleAddMultipleUserTaskAccesses(usersData);
            if (success) {
                const userLogins = usersData
                    .map(data => data.user?.login)
                    .filter(login => login);

                if (userLogins.length > 0 && onSendNotifications) {
                    await onSendNotifications(userLogins, 'permissions_add');
                }

                onUserTaskAccessAdded();
                handleClose();
            }
        } catch (error) {
            console.error('Ошибка добавления пользователей:', error);
            showError('Ошибка добавления пользователей');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            style={{ minHeight: '600px' }}
        >
            <DialogTitle>
                Добавление пользователей в команду
                <Typography variant="body2" color="textSecondary">
                    Шаг {currentStep + 1} из {steps.length}
                </Typography>
            </DialogTitle>

            <DialogContent className="pt-4">
                <Stepper
                    className="mb-4"
                    activeStep={currentStep}
                >
                    {steps.map((label) => (
                        <Step className="step" key={label}>
                            <StepLabel className="step-label" StepIconComponent={CustomStepIcon}>
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderStepContent(currentStep)}
            </DialogContent>

            <DialogActions>
                {currentStep > 0 &&
                    <Button
                        onClick={() => setCurrentStep(prev => prev - 1)}
                        disabled={loading || currentStep === 0}
                        className="dt1520-button active-dt1520-button view-button default-button"
                    >
                        Назад
                    </Button>
                }
                <Button
                    onClick={() => {
                        if (currentStep < steps.length - 1) {
                            setCurrentStep(prev => prev + 1);
                        } else {
                            handleAddUserAccesses();
                        }
                    }}
                    disabled={loading || (currentStep === 0 && selectedUsers.length === 0)}
                    className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                >
                    {loading ? 'Сохранение...' : (currentStep === steps.length - 1 ?
                        `Подтвердить` :
                        'Далее')
                    }
                </Button>
                <Button
                    onClick={handleClose}
                    className="dt1520-button active-dt1520-button view-button default-button"
                >
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
};